import { retryTransient } from '@agos/api-client'
import { useGetCmsPagesSlug } from '@agos/api-client/marketing'
import { T, renderMarkdown, tt } from '@agos/shared'
import { NotFoundScreen } from '@agos/ui'
import { ArrowLeft, Loader2 } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useParams } from 'wouter'

import {
  FRAME_SANDBOX,
  MIN_FRAME_HEIGHT,
  buildFrameDocument,
  heightFromMessage,
  looksLikeHtml,
} from './cms-frame'
import { pageIsMissing } from './cms-page'

export function CmsPagePage() {
  const params = useParams<{ slug: string }>()
  const slug = params.slug ?? ''
  /* A 404 here is an **answer**, not a failure: the slug is whatever the visitor typed or whatever a stale link
     points at. Retried three times it took about seven seconds to admit it, and until then this rendered its ordinary
     body with no title and no content - a blank page, where legacy shows a 404. FB-C55, and the same root cause as
     FB-C48.

     FB-835, §9.549 - **that reasoning is why this is a predicate rather than `retry: false`.** The choice had been
     between a slow 404 and a permanent false state on a page that is really there; `worthRetrying` is neither, because
     it keeps every 4xx except 408 and 429 final while a 500 or a dropped connection gets one more go. FB-829 fixed what
     this screen *says* about a failure; this decides how often it has to say it. */
  const page = useGetCmsPagesSlug(slug, { query: { enabled: slug !== '', retry: retryTransient } })
  const frame = useRef<HTMLIFrameElement>(null)
  const [height, setHeight] = useState(MIN_FRAME_HEIGHT)

  const row = page.data as { title?: unknown; content?: unknown } | undefined
  const title = typeof row?.title === 'string' ? row.title : ''
  const content = typeof row?.content === 'string' ? row.content : ''
  const isHtml = looksLikeHtml(content)

  // Resolved from the theme and handed in, because the frame cannot see the parent's CSS variables.
  const colours = useMemo(() => {
    if (typeof getComputedStyle === 'undefined' || typeof document === 'undefined') {
      return { text: '#1f2937', link: '#2563eb' }
    }
    const style = getComputedStyle(document.documentElement)
    const read = (name: string, fallback: string) => {
      const value = style.getPropertyValue(name).trim()
      return value === '' ? fallback : value
    }
    return { text: read('--foreground', '#1f2937'), link: read('--primary', '#2563eb') }
  }, [])

  useEffect(() => {
    if (!isHtml) return undefined
    function onMessage(event: MessageEvent) {
      // Identity against this frame's own window. An origin string from a sandboxed frame is "null"
      // and proves nothing.
      const next = heightFromMessage(event, frame.current?.contentWindow)
      if (next !== null) setHeight(next)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [isHtml])

  if (page.isLoading) {
    // Nothing rather than an empty article: a heading-less page with no body reads as broken, and the
    // wait is one request.
    return (
      <div className="min-h-[70vh] flex items-center justify-center" data-cms-loading>
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    )
  }

  // **A page that could not be fetched is not a page that does not exist — §9.100.** `retry` is off
  // here, so one 500, or a 429 from the shared limiter, told a customer that *O nás* or *Kontakt* had
  // gone. Only the server saying **404** means missing or unpublished; anything else is this end
  // failing and should say so, with a way to try again that is not guessing a different address.
  /* FB-829, §9.544 - **this was inverted, inside the code written to prevent it**, and it is a named rule now so
     the predicate is testable and reads as its own documentation. `pageIsMissing`'s javadoc carries the measurement:
     `customFetch`'s `await fetch` is unwrapped so a dropped connection rejects with a `TypeError`, and
     `ResponseParseError extends Error` rather than `ApiError` - so the old
     `!(page.error instanceof ApiError) || …` counted both as *missing* and told a customer whose phone lost signal
     that the page does not exist. */
  const missing = pageIsMissing(page.error)

  if (page.isError && !missing) {
    return (
      <NotFoundScreen
        heading={<T>Stránku sa nepodarilo načítať</T>}
        explanation={<T>Skúste to prosím o chvíľu znova.</T>}
        homeLabel={<T>Späť na menu</T>}
      />
    )
  }

  if (page.isError) {
    return (
      <NotFoundScreen
        heading={<T>Stránka nebola nájdená</T>}
        // One clause longer than the router's own 404, and legacy's reason is a good one: a page can be
        // missing because the address is wrong, or because it exists and is not published.
        explanation={<T>Je nám ľúto, ale stránka ktorú hľadáte neexistuje alebo nie je verejná.</T>}
        homeLabel={<T>Späť na menu</T>}
      />
    )
  }

  return (
    <main className="max-w-3xl mx-auto px-4 py-10" data-cms-page={slug}>
      <Link
        href="/"
        data-to-home
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
      >
        <ArrowLeft className="w-4 h-4" />
        <T>Späť na menu</T>
      </Link>
      {title !== '' && <h1 className="text-3xl font-bold text-foreground mb-6">{title}</h1>}
      {isHtml ? (
        <iframe
          ref={frame}
          data-cms-frame
          title={title === '' ? tt('Obsah stránky') : title}
          // `allow-same-origin` is absent on purpose: scripts run, at a null origin, with no access to
          // the customer's session. Replacing this with dangerouslySetInnerHTML would be stored XSS.
          sandbox={FRAME_SANDBOX}
          srcDoc={buildFrameDocument(content, colours)}
          style={{ height }}
          className="w-full border-0 block"
        />
      ) : (
        // §9.169 — markdown, not a pre-wrapped block. Safe to inject because `renderMarkdown`
        // escapes every `<`, `>` and `&` **before** a single rule runs, so the only tags in the
        // string are the ones it writes. A page that is HTML never reaches here: `looksLikeHtml`
        // sends it to the sandboxed frame above, exactly as before.
        <div
          className="prose prose-sm max-w-none text-foreground/90 [&_h1]:text-2xl [&_h1]:font-bold [&_h1]:mt-6 [&_h1]:mb-2 [&_h2]:text-xl [&_h2]:font-bold [&_h2]:mt-5 [&_h2]:mb-2 [&_h3]:text-lg [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-1.5 [&_p]:mb-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-3 [&_li]:mb-1 [&_a]:text-primary [&_a]:underline [&_blockquote]:border-l-4 [&_blockquote]:border-border [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3 [&_hr]:my-6 [&_hr]:border-border [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:rounded-lg [&_pre]:overflow-x-auto [&_pre]:my-3 [&_strong]:font-bold"
          data-cms-text
          dangerouslySetInnerHTML={{ __html: renderMarkdown(content) }}
        />
      )}
    </main>
  )
}
